<script lang="ts">
  import type { Coord } from '~/types';
  import GameOver, { gameOverModal } from './GameOver.svelte';
  import { shuffle, shuffleBoard } from '~/algorithms/shuffle';
  import { seededRng } from '~/algorithms/rng';
  import { animationSpeed } from '~/animations/utils.svelte';
  import { delay } from '~/animations/utils.svelte';
  import GameBoard from '~/board/Board.svelte';
  import { gamesPlayed } from '~/store/gameState.svelte';
  import WordContainer from '~/components/WordContainer.svelte';
  import Gauge from '~/gauge/daily/Gauge.svelte';
  import Shuffle from '~/pills/Shuffle.svelte';
  import { saveDailyAnalytics } from '~/lib/mutations.svelte';
  import { onMount, tick } from 'svelte';
  import Search from '~/pills/Search.svelte';
  import {
    isLatestDaily,
    isPremium,
    latestDailyQuery,
    winStreakQuery,
  } from '~/lib/queries.svelte';
  import { nudgePremium } from '~/modals/PremiumModal.svelte';
  import ReplayGameButton from '~/components/buttons/ReplayGameButton.svelte';
  import PuzzleDateButton from './PuzzleDateButton.svelte';
  import { fade } from 'svelte/transition';
  import {
    growAnimation,
    sendWordAnimation,
    scoreWordAnimation,
    crossing,
  } from '~/animations/crossfade.svelte';
  import {
    collapsing,
    flipAnimation,
    shuffling,
  } from '~/animations/flip.svelte';
  import DrawerButton from '~/components/buttons/DrawerButton.svelte';
  import { shareModal } from '~/share/ShareModal.svelte';
  import FooterSpacer from '~/components/FooterSpacer.svelte';
  import { tutorialViewed } from '~/tutorial/state.svelte';
  import { dictionary } from '~/algorithms/dictionary.svelte';
  import { getGame } from '~/store/GameContext.svelte';
  import { DailyGame } from '~/store/daily.svelte';
  import { getDimensions } from '~/utils/DimensionsContext.svelte';
  import TutorialButton from '~/tutorial/TutorialButton.svelte';

  const dims = getDimensions();
  const premium = isPremium();

  let containerWidth = $state(0);
  let containerHeight = $state(0);
  let hint = $state<string>();

  const game = $derived(getGame<DailyGame>().current);

  const saveDaily = saveDailyAnalytics();

  const ws = $derived(
    winStreakQuery({
      mode: game.variant,
      enabled: Boolean(game && premium.data),
    }),
  );

  const latest = $derived(latestDailyQuery(game.variant));
  const isLatest = $derived(isLatestDaily(game, latest.data));

  onMount(async () => {
    // if game was left in incomplete clear state
    if (game.turns > 0 && !game.lost) {
      game.disabled = true;
      await delay(500);
      await handleScore();
      game.disabled = false;
    } else if (game.lost) {
      game.disabled = true;
      await delay(100);
      gameOverModal.push();
    }
  });

  const handleEndTurn = async () => {
    hint = await dictionary.boardHasWord($state.snapshot(game.board));
    // TODO I think this should be blocked after first invokation
    if (!game.board.length || !hint || game.swaps <= 0) {
      game.disabled = true;
      await tick();
      let timeout = 800;
      for (let j = 0; j < (game.board[0]?.length ?? 0); j++) {
        for (let i = 0; i < game.board.length; i++) {
          const tile = game.board[i][j];
          if (tile) {
            tile.flipped = true;
            await tick();
            game.board[i][j] = {
              id: tile.id,
              flipped: true,
              flips: tile.flips,
              letter: tile.letter,
              points: game.variant === '5x5' ? -1 : -tile.points!,
            };
            if (game.variant === '5x5') {
              game.swaps--;
            } else {
              game.swaps -= tile.points!;
            }
          }
          await delay(timeout);
          timeout *= 0.9;
        }
      }
      game.finalBoard = $state
        .snapshot(game.board)
        .map((col) => col.map((t) => t));
      game.fullClear = game.isFullClear;
      await delay(1000);
      // mark puzzle completed and update cache
      game.lost = true;
    }
    if (game.lost) {
      await handleGameOver();
    } else {
      game.disabled = false;
    }
    return game.lost;
  };

  const handleGameOver = async () => {
    if (game.endedAt) return;
    game.endedAt = Date.now();
    game.duration = Math.round((game.endedAt - game.startedAt!) / 1000);
    game.disabled = true;
    game.highlighted = {};
    gamesPlayed.current++;
    game.setStars();

    // don't update local stats if replaying daily
    // needs to happen before pushing gameOverModal
    if (isLatest) {
      await game.saveStats();
    }

    await gameOverModal.push();
    if (gamesPlayed.current === 2) {
      await shareModal.push();
    } else if (!premium.data) {
      await nudgePremium(gamesPlayed.current);
    }
    await game.save();
    saveDaily.mutate(game);
    // TODO surface count somewhere in UI
  };

  // chain is incremented on subsequent recursive calls of handleScore
  const handleScore = async (chain = 0) => {
    const coordsToCheck: Coord[] = [
      ...game.board.map((_, i): Coord => [i, 0]),
      ...(game.board[0]?.map((_, j): Coord => [0, j])?.slice(1) ?? []), // removing duplicate [0, 0]
    ];
    const { words, intersections } = await dictionary.findWords(
      $state.snapshot(game.board),
      game.mode,
      coordsToCheck,
    );

    // recursive base case
    if (!words.length) {
      return await handleEndTurn();
    } else {
      tutorialViewed.current = true;
    }

    game.highlightWords(words);
    game.intersections = intersections;

    for (const word of words) {
      await game.appendWord(word);
      // this has to happen outside appendWord for reactivity
      game.latestWord = word;
      // clear letters, flying them (and their shadows) to the word pile
      await crossing.launch(word, intersections, () => game.removeLetters(word));

      const wordChain = game.words - game.prevWordPointer;
      game.setMarqueeText(word, wordChain);

      await game.save();
      // allow animations to play
      await growAnimation.settled();
      await sendWordAnimation.settled();
    }

    // wait for full crossfade (including FADE_DUR) before board reshape
    await scoreWordAnimation.settled();

    // TODO better abstraction of collapsing toggle
    collapsing.current = true;
    game.collapseBoard();

    await delay(animationSpeed.ms / 2);
    collapsing.current = false;
    // increment chain only if no other words on board
    await handleScore(chain + words.length);
    game.marquee = undefined;

    // game is not lost
    await game.save();
    return false;
  };

  const handleShuffle = async () => {
    if (!game.disabled) {
      game.disabled = true;
      game.shuffles--;
      shuffling.current = true;
      await tick();
      const coords = Array.from({ length: game.board.length }).flatMap((_, i) =>
        Array.from({ length: game.board[0].length }).map(
          (_, j): Coord => [i, j],
        ),
      );

      const rng = seededRng(game.id, game.turns + game.score);
      const shuffled = shuffle([...coords], rng);
      game.board = shuffleBoard(game.board, coords, shuffled);
      // pause for animation
      await flipAnimation.settled();
      game.appendTurn(shuffled);
      await game.save();

      shuffling.current = false;
      await handleScore(0);
    }
  };
</script>

<div
  class="top-container"
  style="--board-width: min({dims.board.width}px, 100%)"
  bind:clientWidth={containerWidth}
  bind:clientHeight={containerHeight}
>
  <DrawerButton />
  <PuzzleDateButton />
  <Gauge />
  {#if !game.lost}
    <div class="bottom-right-controls" transition:fade>
      {#if isLatest}
        <Shuffle shuffles={game.shuffles} onShuffle={handleShuffle} />
      {:else}
        <ReplayGameButton disabled={game.disabled} iconOnly />
      {/if}
    </div>
    <div class="bottom-left-controls">
      <TutorialButton iconOnly />
      <Search {containerWidth} {containerHeight} />
    </div>
  {/if}
</div>
<GameBoard bind:disabled={game.disabled} {handleScore} {handleEndTurn} />
<FooterSpacer />
{#if game.lost}
  {#await game.getPage('length', 0) then words}
    <GameOver {words} />
  {/await}
{:else if game.words}
  <WordContainer />
{/if}

<style lang="scss">
  @use '~/theme/include-media' as *;

  .bottom-right-controls {
    position: absolute;
    bottom: 0;
    right: 0;
  }
  .bottom-left-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    display: flex;
    gap: 4px;
  }
  .top-container {
    position: relative;
    @media (min-aspect-ratio: 1 / 1) {
      width: var(--board-width);
    }
    @media (max-aspect-ratio: 1 / 1) {
      width: 100%;
    }
    @include media('<=phone') {
      font-size: 0.8rem;
    }
    @include media('>phone', '<=tablet') {
      font-size: 1rem;
    }
    @include media('>tablet') {
      font-size: 1.2rem;
    }
    :global(.pill) {
      position: absolute;
      bottom: 0;
      left: 0;
    }
  }
</style>
